import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateTime, serviceLabels } from "@/lib/format";
import type { PublicTracking } from "@/lib/types";
import { Alert, Button, Card, Skeleton, StatusBadge } from "@/components/ui";
import { Timeline } from "@/components/Timeline";
import { LogoMark } from "@/components/Logo";
import { SiteFooter } from "@/components/SiteFooter";

/**
 * Public tracking. No login, no account context: the waybill is the credential,
 * so the API returns a deliberately thin payload with no addresses or prices.
 */
export function Track() {
  const { waybill = "" } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(waybill);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["track", waybill],
    queryFn: () => api.public<PublicTracking>(`/v1/track/${encodeURIComponent(waybill)}`),
    enabled: waybill.length > 0,
    retry: false,
    refetchInterval: 120_000,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (input.trim()) navigate(`/track/${encodeURIComponent(input.trim())}`);
  }

  return (
    <div className="min-h-dvh bg-ink-100">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <LogoMark className="h-7 w-7" />
            WeShip
          </Link>
          <Link to="/login" className="text-sm font-medium text-brand">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">Track a shipment</h1>
        <form onSubmit={submit} className="mt-4 flex gap-2">
          <input
            value={input}
            onChange={(event) => setInput(event.target.value.toUpperCase())}
            placeholder="WS260906ABC123X"
            aria-label="Waybill number"
            className="ws-input font-mono"
          />
          <Button type="submit">Track</Button>
        </form>

        {waybill && isLoading && (
          <Card className="mt-6">
            <Skeleton className="h-40 w-full" />
          </Card>
        )}

        {waybill && isError && (
          <div className="mt-6">
            <Alert tone="warn">
              We could not find that waybill. Check the number and try again, or contact the sender.
            </Alert>
          </div>
        )}

        {data && (
          <Card className="mt-6">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 pb-5">
              <div>
                <p className="font-mono text-sm text-ink-400">{data.waybill}</p>
                <p className="mt-1 text-xl font-semibold tracking-tight">
                  To {data.destination_suburb}, {data.destination_city}
                </p>
                <p className="mt-1 text-sm text-ink-400">
                  {serviceLabels[data.service_level] ?? data.service_level}, {data.parcel_count} parcel
                  {data.parcel_count === 1 ? "" : "s"}
                </p>
              </div>
              <div className="text-right">
                <StatusBadge status={data.status} />
                <p className="mt-2 text-sm text-ink-400">
                  {data.delivered_at
                    ? `Delivered ${dateTime(data.delivered_at)}`
                    : data.estimated_delivery_at
                      ? `Due ${dateTime(data.estimated_delivery_at)}`
                      : "Estimate pending"}
                </p>
              </div>
            </div>
            <div className="pt-5">
              <Timeline events={data.events} />
            </div>
          </Card>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
